/**
 * Auth Shell Application
 *
 * This is a minimal authentication shell (~50KB) that handles Frontegg login.
 * It's served to unauthenticated users at the root path (/).
 *
 * ## Purpose
 *
 * The security-engineer app uses a split architecture to prevent unauthenticated
 * users from accessing the main app's JavaScript bundle, which would expose:
 * - API endpoints and schemas
 * - Implementation details and business logic
 * - Feature flags and configuration
 *
 * ## Flow
 *
 * 1. User visits app.tunesecurity.com
 * 2. Lambda@Edge sees no valid token -> serves this auth shell
 * 3. Auth shell redirects to Frontegg hosted login
 * 4. After login, Frontegg redirects back here
 * 5. Auth shell extracts token and sets cookie on parent domain
 * 6. Redirects to /app/ where Lambda@Edge validates and serves main app
 *
 * ## Cookie Workaround
 *
 * Frontegg stores tokens in cookies on .auth.tunesecurity.com, but Lambda@Edge
 * runs at app.tunesecurity.com and can't read those cookies. We work around this
 * by extracting the access token from the Frontegg SDK and setting it as a cookie
 * on .tunesecurity.com (parent domain) so Lambda@Edge can read it.
 *
 * The cookie is not HttpOnly (can't be set from JS), but the access token is
 * short-lived and all API calls validate the JWT signature server-side.
 */

import { useAuth, useLoginWithRedirect } from '@frontegg/react';
import { useEffect } from 'react';
import { setAccessTokenCookie } from '@tune/frontegg-react';

/**
 * Auth shell app - following Frontegg's official integration guide.
 * Redirects unauthenticated users to hosted login, then to /app/ on success.
 * Sets access token cookie on parent domain for Lambda@Edge validation.
 */
export function App() {
  const { user, isAuthenticated } = useAuth();
  const loginWithRedirect = useLoginWithRedirect();

  useEffect(() => {
    if (!isAuthenticated) {
      void loginWithRedirect();
    }
  }, [isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated && user?.accessToken) {
      // Set access token cookie on parent domain for Lambda@Edge
      setAccessTokenCookie(user.accessToken);
      window.location.href = '/app/';
    }
  }, [isAuthenticated, user?.accessToken]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      Loading...
    </div>
  );
}
